import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { ClientService } from '@app/core/client/services/client.service';
import {
  BusinessAccountContact,
  BusinessAccountContactMeta,
} from '@app/core/client/store/client.model';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';

import { ClientContactApiService } from './client-contact-api.service';

@Injectable({
  providedIn: 'root',
})
export class ClientContactUtilsService {
  private readonly _latestActiveClientPrimaryContact =
    new BehaviorSubject<BusinessAccountContact>(<BusinessAccountContact>{});
  public latestActiveClientPrimaryContact$ =
    this._latestActiveClientPrimaryContact.asObservable();

  constructor(
    private featureToggleService :FeatureToggleService,
    private clientContactApi :ClientContactApiService,
    private clientService :ClientService,
    private toastr :ToastrService,
  ) {}

  addToActiveClientPrimaryContacts(
    userAccount :BusinessAccountContact,
  ) :Observable<any> {
    return this.trySetUserAsPrimaryContact(
      userAccount.business_account_id,
      userAccount.id,
    ).pipe(
      tap(() => {
        this._latestActiveClientPrimaryContact.next(userAccount);
      }),
    );
  }

  removeFromActiveClientPrimaryContacts(
    userAccount :BusinessAccountContact,
    flag ?:string,
  ) :Observable<any> {
    return this.tryUnsetUserAsPrimaryContact(
      userAccount.business_account_id,
      userAccount.id,
      flag,
    ).pipe(
      tap(() => {
        userAccount.primary_contact = false;
        this._latestActiveClientPrimaryContact.next(userAccount);
      }),
    );
  }

  updatePrimaryContactStatus({
    businessAccountId,
    userAccountId,
    asPrimaryContact,
  }) :Observable<any> {
    if (asPrimaryContact) {
      return this.trySetUserAsPrimaryContact(businessAccountId, userAccountId);
    }

    return this.tryUnsetUserAsPrimaryContact(businessAccountId, userAccountId);
  }

  transformUserMetaToKeyValue(metas :BusinessAccountContactMeta[]) :any {
    const metaData :any = {};
    metas.forEach((meta :BusinessAccountContactMeta) => {
      metaData[meta.key] = meta.value;
    });

    return metaData;
  }

  private trySetUserAsPrimaryContact(
    businessAccountId :number,
    userAccountId :number,
  ) :Observable<any> {
    return this.clientContactApi
      .setUserAsPrimaryContact(businessAccountId, userAccountId)
      .pipe(
        tap(
          (respMessage :string) => {
            this.toastr.success('Successfully set as primary contact');
          },
          (error) => {
            this.toastr.error('Error request. Try again later.');
          },
        ),
      );
  }

  private tryUnsetUserAsPrimaryContact(
    businessAccountId :number,
    userAccountId :number,
    flag ?:string,
  ) :Observable<any> {
    return this.clientContactApi
      .unsetUserAsPrimaryContact(businessAccountId, userAccountId)
      .pipe(
        tap(
          (respMessage :string) => {
            if (flag !== 'delete') {
              this.toastr.success('Successfully unset as primary contact');
            }
          },
          (error) => {
            this.toastr.error('Error request. Try again later.');
          },
        ),
      );
  }
}
